import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getSubmissionFormsList, getSubmissionFormsMinimal, deleteSubmissionForm, setSubmissionFormsPage } from '../../actions/submissionFormActions';
import { getCoursesMinimal } from '../../actions/courseActions';
import { getTags } from '../../actions/tagActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import TableView from '../content-management/views/TableView';
import MobileView from '../content-management/views/MobileView';
import TableSkeleton from '../content-management/views/TableView/TableSkeleton';
import MobileSkeleton from '../content-management/views/MobileView/MobileSkeleton';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import { Plus, Filter, X, Calendar, ClipboardList, Check } from 'lucide-react';
import { loadContentManagementFilters, saveContentManagementFilters } from '../../utils/contentManagementFilters';
import { isAdminRole } from '../../utils/roleHelpers';

const SubmissionFormsManagementContainer = ({
  auth: { user },
  submissionForm: { submissionForms, minimalSubmissionForms, loading, currentPage, totalPages, totalSubmissionForms, hasNextPage, hasPrevPage },
  course: { coursesMinimal },
  tag: { tags },
  getSubmissionFormsList,
  deleteSubmissionForm,
  getTags,
  setSubmissionFormsPage,
  getSubmissionFormsMinimal,
  getCoursesMinimal
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [selectedAutoApproval, setSelectedAutoApproval] = useState(null);
  const [selectedRepeatable, setSelectedRepeatable] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersLoadedRef = useRef(false);

  // Admin-only feature
  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    getTags();
    getSubmissionFormsMinimal();
    getCoursesMinimal();
  }, [getTags, getSubmissionFormsMinimal, getCoursesMinimal]);

  useEffect(() => {
    const saved = loadContentManagementFilters('Submission Forms');
    if (saved?.filters) {
      setSelectedTitles(saved.filters.selectedTitles || []);
      setSelectedCourses(saved.filters.selectedCourses || []);
      setSelectedRequirements(saved.filters.selectedRequirements || []);
      setSelectedAutoApproval(
        typeof saved.filters.selectedAutoApproval === 'boolean' ? saved.filters.selectedAutoApproval : null
      );
      setSelectedRepeatable(
        typeof saved.filters.selectedRepeatable === 'boolean' ? saved.filters.selectedRepeatable : null
      );
      setSelectedTags(saved.filters.selectedTags || []);
      if (saved.count > 0) {
        setShowFilters(true);
      }
    }
    filtersLoadedRef.current = true;
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const filters = {};
    if (selectedTitles.length > 0) {
      filters.title = selectedTitles;
    }
    if (selectedCourses.length > 0) {
      filters.course = selectedCourses;
    }
    if (selectedRequirements.length > 0) {
      filters.requirements = selectedRequirements;
    }
    if (selectedAutoApproval !== null) {
      filters.autoApproval = selectedAutoApproval;
    }
    if (selectedRepeatable !== null) {
      filters.repeatable = selectedRepeatable;
    }
    if (selectedTags.length > 0) {
      filters.tags = selectedTags;
    }
    getSubmissionFormsList(currentPage, 10, filters);
  }, [filtersHydrated, currentPage, selectedTitles, selectedCourses, selectedRequirements, selectedAutoApproval, selectedRepeatable, selectedTags, getSubmissionFormsList]);

  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    const count = selectedTitles.length + selectedCourses.length + selectedRequirements.length + selectedTags.length +
      (selectedAutoApproval !== null ? 1 : 0) + (selectedRepeatable !== null ? 1 : 0);
    saveContentManagementFilters('Submission Forms', {
      selectedTitles,
      selectedCourses,
      selectedRequirements,
      selectedAutoApproval,
      selectedRepeatable,
      selectedTags
    }, count);
  }, [selectedTitles, selectedCourses, selectedRequirements, selectedAutoApproval, selectedRepeatable, selectedTags]);

  const handleAdd = () => {
    navigate('/content-management/submission-forms/new');
  };

  const handleView = (item) => {
    navigate(`/content-management/submission-forms/${item._id}/view`);
  };

  const handleEdit = (item) => {
    navigate(`/content-management/submission-forms/${item._id}/edit`);
  };

  const handleCopyUrl = (item) => {
    const url = `${window.location.origin}/submissions/${item._id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(item._id);
      setTimeout(() => setCopySuccess(null), 2000);
    });
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    deleteSubmissionForm(itemToDelete._id);
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Submission Forms') {
      // Already on submission forms page, do nothing
      return;
    }
    // Navigate to content management with selected tab
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  const getItemTitle = (item) => {
    return item.title;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Handle pagination - only update Redux state, useEffect will fetch data
  const handlePageChange = (newPage) => {
    setSubmissionFormsPage(newPage);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearAllFilters = () => {
    setSelectedTitles([]);
    setSelectedCourses([]);
    setSelectedRequirements([]);
    setSelectedAutoApproval(null);
    setSelectedRepeatable(null);
    setSelectedTags([]);
    setSubmissionFormsPage(1);
  };

  const hasActiveFilters = selectedTitles.length > 0 || selectedCourses.length > 0 || 
    selectedRequirements.length > 0 || selectedAutoApproval !== null || 
    selectedRepeatable !== null || selectedTags.length > 0;

  // Get unique values from submission forms
  const uniqueTitles = minimalSubmissionForms && minimalSubmissionForms.length > 0
    ? [...new Set(minimalSubmissionForms.map(sf => sf.title))].filter(Boolean)
    : [];
    
  const uniqueCourses = coursesMinimal && coursesMinimal.length > 0
    ? coursesMinimal
    : [];
    
  const requirementOptions = ['Link', 'Comment', 'Achievement Image'];

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      {/* Copy URL success toast */}
      {copySuccess && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg animate-fade-in">
          <Check className="w-4 h-4" />
          <span className="text-sm font-medium">Student URL copied to clipboard!</span>
        </div>
      )}
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Submission Forms"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={false}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-[#054581]">
                  Manage Submission Forms
                </h2>
                {totalSubmissionForms > 0 && (
                  <span className="text-sm text-gray-500">
                    ({totalSubmissionForms} total)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    hasActiveFilters
                      ? 'bg-[#1086a4] text-white shadow-md'
                      : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                  }`}
                >
                  <Filter size={18} />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {hasActiveFilters && (
                    <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                      Active
                    </span>
                  )}
                </button>
                <button
                  onClick={handleAdd}
                  className="bg-[#1086a4] text-white px-2 sm:px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  <span>Add</span>
                </button>
              </div>
            </div>

            {/* Filter UI */}
            {showFilters && (
              <div className="mb-6 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#054581]">Filter Submission Forms</h3>
                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      hasActiveFilters
                        ? 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <X className="w-3 h-3" />
                    Clear All Filters
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <MultiSelectSearchFilter
                    items={uniqueTitles.map(title => ({ _id: title, title: title }))}
                    selectedItems={selectedTitles}
                    onSelectionChange={(selected) => {
                      setSelectedTitles(selected);
                      setSubmissionFormsPage(1);
                    }}
                    title="Titles"
                    placeholder="Search titles..."
                    emptyMessage="No titles available"
                  />

                  <MultiSelectSearchFilter
                    items={uniqueCourses.map(course => ({ _id: course._id, title: course.title }))}
                    selectedItems={selectedCourses}
                    onSelectionChange={(selected) => {
                      setSelectedCourses(selected);
                      setSubmissionFormsPage(1);
                    }}
                    title="Courses"
                    placeholder="Search courses..."
                    emptyMessage="No courses available"
                  />

                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200">
                      <div className="flex items-center gap-2">
                        <ClipboardList className="w-4 h-4 text-[#1086a4]" />
                        <span className="text-sm font-medium text-[#054581]">Filter by Requirements</span>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedRequirements([]);
                          setSubmissionFormsPage(1);
                        }}
                        disabled={selectedRequirements.length === 0}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                          selectedRequirements.length > 0
                            ? 'bg-red-500 hover:bg-red-600 text-white shadow-sm hover:shadow-md transform hover:scale-105 cursor-pointer'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-75'
                        }`}
                      >
                        <X className="w-3 h-3" />
                        {selectedRequirements.length > 0 ? `Clear all (${selectedRequirements.length})` : 'Clear all'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          setSelectedRequirements([]);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedRequirements.length === 0
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        All
                      </button>
                      {requirementOptions.map(option => {
                        const isActive = selectedRequirements.includes(option);
                        return (
                          <button
                            key={option}
                            onClick={() => {
                              const next = isActive
                                ? selectedRequirements.filter(item => item !== option)
                                : [...selectedRequirements, option];
                              setSelectedRequirements(next);
                              setSubmissionFormsPage(1);
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              isActive
                                ? 'bg-[#1086a4] text-white shadow-sm'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                            }`}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <MultiSelectSearchFilter
                    items={tags}
                    selectedItems={selectedTags}
                    onSelectionChange={(selected) => {
                      setSelectedTags(selected);
                      setSubmissionFormsPage(1);
                    }}
                    title="Tags"
                    placeholder="Search tags..."
                    emptyMessage="No tags available"
                  />
                </div>

                {/* Boolean Filters */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Auto Approval</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedAutoApproval(null);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedAutoApproval === null
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAutoApproval(true);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedAutoApproval === true
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAutoApproval(false);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedAutoApproval === false
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-medium text-[#054581] mb-2">Repeatable</label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedRepeatable(null);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedRepeatable === null
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRepeatable(true);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedRepeatable === true
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => {
                          setSelectedRepeatable(false);
                          setSubmissionFormsPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          selectedRepeatable === false
                            ? 'bg-[#1086a4] text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:border-[#1086a4]'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <>
                {/* Desktop Skeleton */}
                <div className="hidden md:block">
                  <TableSkeleton rowCount={10} activeTab="Submission Forms" />
                </div>

                {/* Mobile Skeleton */}
                <div className="md:hidden">
                  <MobileSkeleton cardCount={10} />
                </div>
              </>
            ) : (
              <>
                {/* Mobile View */}
                <div className="md:hidden">
                  <MobileView
                    items={submissionForms}
                    activeTab="Submission Forms"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={false}
                    onCopyUrl={handleCopyUrl}
                  />
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                  <TableView
                    items={submissionForms}
                    activeTab="Submission Forms"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={false}
                    onCopyUrl={handleCopyUrl}
                  />
                </div>

                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalSubmissionForms}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  itemName="submission forms"
                />
              </>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteConfirmation}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
          itemType="Submission Form"
        />
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  submissionForm: state.submissionForm,
  tag: state.tag,
  course: state.course
});

export default connect(
  mapStateToProps,
  {
    getSubmissionFormsList,
    getSubmissionFormsMinimal,
    deleteSubmissionForm,
    getTags,
    setSubmissionFormsPage,
    getCoursesMinimal
  }
)(SubmissionFormsManagementContainer);
